import { Box, Button, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { AcademyLesson } from '../../../pages/academy/Academy/AcademyService';
import {
  learningContentStageClassNames,
  learningContentStageStyles,
} from './LearningContentStage.styles';

export interface LearningContentStageProps {
  skill: AcademyLesson;
  isComplete: boolean;
  isChecking: boolean;
  onComplete: () => void;
}

export function LearningContentStage({
  skill,
  isComplete,
  isChecking,
  onComplete,
}: LearningContentStageProps) {
  const theme = useTheme();
  const sourceUrl = skill.learnContent.sourceUrl;
  const isCompletionBlocked = isComplete || isChecking;

  function handleComplete() {
    if (!isCompletionBlocked) {
      onComplete();
    }
  }

  if (sourceUrl && isVideoContent(skill.learnContent.contentType)) {
    return (
      <Box
        component="video"
        className={learningContentStageClassNames.video}
        sx={learningContentStageStyles.video()}
        controls
        playsInline
        preload="metadata"
        src={sourceUrl}
        aria-label={`${skill.learnContent.title} video`}
        onEnded={handleComplete}
      >
        <a href={sourceUrl} target="_blank" rel="noreferrer">
          Open {skill.learnContent.title}
        </a>
      </Box>
    );
  }

  if (sourceUrl) {
    return (
      <Paper
        className={learningContentStageClassNames.placeholder}
        elevation={0}
        sx={learningContentStageStyles.placeholder(theme)}
      >
        <Box
          className={learningContentStageClassNames.titleCard}
          sx={learningContentStageStyles.titleCard()}
        >
          <Typography sx={learningContentStageStyles.pathLabel(theme)}>
            {getPathLabel(skill.pathKey)}
          </Typography>
          <Typography component="h4" sx={learningContentStageStyles.title(theme)}>
            {skill.learnContent.title}
          </Typography>
        </Box>
        <Button
          component="a"
          className={learningContentStageClassNames.link}
          sx={learningContentStageStyles.link()}
          href={sourceUrl}
          target="_blank"
          rel="noreferrer"
          disableRipple
          onClick={handleComplete}
        >
          Open learning content
        </Button>
      </Paper>
    );
  }

  return (
    <Button
      className={`${learningContentStageClassNames.placeholder} ${learningContentStageClassNames.placeholderButton}`}
      sx={learningContentStageStyles.placeholderButtonFrame(theme)}
      type="button"
      disableRipple
      disabled={isComplete || isChecking}
      aria-label={getLearningContentCompleteLabel(skill)}
      onClick={handleComplete}
    >
      <Box
        className={learningContentStageClassNames.titleCard}
        sx={learningContentStageStyles.titleCard()}
      >
        <Box
          className={learningContentStageClassNames.orbit}
          sx={learningContentStageStyles.orbit(theme)}
          aria-hidden="true"
        >
          <Box component="span" />
        </Box>
        <Typography sx={learningContentStageStyles.pathLabel(theme)}>
          {getPathLabel(skill.pathKey)}
        </Typography>
        <Typography component="h4" sx={learningContentStageStyles.title(theme)}>
          {skill.learnContent.title}
        </Typography>
      </Box>
      <Box
        className={learningContentStageClassNames.controls}
        sx={learningContentStageStyles.controls()}
        aria-hidden="true"
      >
        <Box
          component="span"
          className={learningContentStageClassNames.playControl}
          sx={learningContentStageStyles.playControl()}
        />
        <Box
          component="span"
          className={learningContentStageClassNames.volumeControl}
          sx={learningContentStageStyles.volumeControl()}
        />
        <Box
          component="span"
          className={learningContentStageClassNames.timelineControl}
          sx={learningContentStageStyles.timelineControl()}
        >
          <Box component="span" />
        </Box>
        <Box
          component="span"
          className={learningContentStageClassNames.timeControl}
          sx={learningContentStageStyles.timeControl()}
        >
          -{skill.learnContent.estimatedMinutes}:00
        </Box>
        <Box
          component="span"
          className={learningContentStageClassNames.screenControl}
          sx={learningContentStageStyles.screenControl()}
        />
      </Box>
    </Button>
  );
}

function isVideoContent(contentType: AcademyLesson['learnContent']['contentType']): boolean {
  return contentType === 'video' || contentType === 'interactive_video';
}

function getLearningContentCompleteLabel(skill: AcademyLesson): string {
  return `Complete ${skill.learnContent.title}`;
}

function getPathLabel(pathKey: AcademyLesson['pathKey']): string {
  const labels: Record<AcademyLesson['pathKey'], string> = {
    plan_prepare: 'Planning and Prep',
    teach_facilitate: 'Teach and Facilitate',
    assess_adjust: 'Assess and Adjust',
  };

  return labels[pathKey] || 'Academy Skill';
}
